import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePlus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import { IWebsiteBuilderWebsiteVersionNavigationMenuDropdownItem, IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem, TWebsiteBuilderWebsiteVersionPage } from "redux/reducers/models/websiteBuilder";
import { AddPageView } from "./addPage/AddPageView";
import { WebsiteBuilderToolbarMenuView } from "../../WebsiteBuilderToolbarMenuView";
import { RenamePageView } from "./renamePage/RenamePageView";
import { NavigationMenuItems } from "./NavigationMenuItems";
import "./managePagesView.scss";
import { AddLinkView } from "./addLink/AddLinkView";
import { UpdateLinkView } from "./updateLink/UpdateLinkView";
import Spin from "components/spin/spin";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderAddNavigationMenuItem } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { RenameDropdownView } from "./renameDropdown/RenameDropdownView";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import classNames from "classnames";

export interface IRenamePageSubView {
  type: "rename-page";
  page: TWebsiteBuilderWebsiteVersionPage;
}

export interface IUpdateLinkSubView {
  type: "update-link";
  item: IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem;
}

export interface IRenameDropdownSubView {
  type: "rename-dropdown";
  item: IWebsiteBuilderWebsiteVersionNavigationMenuDropdownItem;
}

interface IAddLinkView {
  type: "add-link";
}

interface IAddPageSubView {
  type: "add-page";
}

interface INoSubView {
  type: "none";
}

export type TManagePagesViewSubView =
  | IRenamePageSubView
  | IUpdateLinkSubView
  | IRenameDropdownSubView
  | IAddLinkView
  | IAddPageSubView
  | INoSubView;

export function ManagePagesView() {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();

  const websiteVersion = useAppSelector(store => store.websiteBuilderStore.editor.websiteVersion);

  const pagesAutoSaveStatus = useAppSelector(store => store.websiteBuilderStore.editor.autoSaveStatusState.pages);
  const pagesAutoSaveStatusNotSaved = useMemo(() => Object.values(pagesAutoSaveStatus).some(status => status !== "saved"), [pagesAutoSaveStatus]);
  const pagesAutoSaveStatusSaveInProgress = useMemo(() => Object.values(pagesAutoSaveStatus).some(status => status === "unsaved-changes" || status === "saving"), [pagesAutoSaveStatus]);

  const [subView, setSubView] = useState<TManagePagesViewSubView>({ type: "none" });

  const [navigationMenuOptionsDropdownOpen, setNavigationMenuOptionsDropdownOpen] = useState<boolean>(false);

  const navigationMenuOptionsDropdownRef = useOnclickOutside(() => {
    setNavigationMenuOptionsDropdownOpen(false);
  });

  function addDropdown() {
    setNavigationMenuOptionsDropdownOpen(false);

    void dispatch(websiteBuilderAddNavigationMenuItem(
      {
        website_version_id: websiteVersion.id,
        page_id: null,
        type: "dropdown",
        label: "Dropdown",
        external_url: null,
        open_in_new_tab: false,
        parent_id: null,
      },
      t,
      goBackToParentView,
    ));
  }

  function renderMenuSection() {
    return (
      <NavigationMenuItems
        disabled={pagesAutoSaveStatusNotSaved}
        setSubView={setSubView}
      />
    );
  }

  function renderActionsSection() {
    return (
      <div className="website-builder-manage-pages-view-actions">
        <div
          ref={navigationMenuOptionsDropdownRef}
          className="manage-pages-view-navigation-menu-options-dropdown"
        >
          <div
            className={classNames("navigation-menu-options-dropbutton", {
              "navigation-menu-options-dropbutton-disabled": pagesAutoSaveStatusNotSaved,
            })}
            onClick={e => {
              if (pagesAutoSaveStatusNotSaved) {
                return;
              }

              e.stopPropagation();

              setNavigationMenuOptionsDropdownOpen(prev => !prev);
            }}
            
          >
            <FontAwesomeIcon className="navigation-menu-options-dropbutton-icon" icon={faPlus} />
          </div>
          <div
            className="navigation-menu-options-content"
            style={{
              display: navigationMenuOptionsDropdownOpen ? "block" : "none",
            }}
          >
            <div className="navigation-menu-options-header">{t("Menu item")}</div>
            <button
              className="navigation-menu-options-option"
              onClick={addDropdown}
              disabled={pagesAutoSaveStatusNotSaved}
            >
              {t("Dropdown")}
            </button>
            <button
              className="navigation-menu-options-option"
              onClick={() => setSubView({ type: "add-link" })}
              disabled={pagesAutoSaveStatusNotSaved}
            >
              {t("Link")}
            </button>
          </div>
        </div>
        <Button
          className="manage-pages-view-add-page-action"
          onClick={() => setSubView({ type: "add-page" })}
          disabled={pagesAutoSaveStatusNotSaved}
        >
          <FontAwesomeIcon className="add-page-action-icon" icon={faFilePlus} />
          {t("Add page")}
        </Button>
      </div>
    );
  }

  function goBackToParentView() {
    setSubView({ type: "none" });
  }

  function renderManagePagesView() {
    switch (subView.type) {
      case "rename-page":
        return (
          <RenamePageView
            page={subView.page}
            disabled={pagesAutoSaveStatusNotSaved}
            goBackToParentView={goBackToParentView}
          />
        );
      case "update-link":
        return (
          <UpdateLinkView
            item={subView.item}
            disabled={pagesAutoSaveStatusNotSaved}
            goBackToParentView={goBackToParentView}
          />
        );
      case "rename-dropdown":
        return (
          <RenameDropdownView
            item={subView.item}
            disabled={pagesAutoSaveStatusNotSaved}
            goBackToParentView={goBackToParentView}
          />
        );
      case "add-link":
        return (
          <AddLinkView
            disabled={pagesAutoSaveStatusNotSaved}
            goBackToParentView={goBackToParentView}
          />
        );
      case "add-page":
        return (
          <AddPageView
            disabled={pagesAutoSaveStatusNotSaved}
            goBackToParentView={goBackToParentView}
          />
        );
      case "none":
      default:
        return (
          <WebsiteBuilderToolbarMenuView
            title={t("Manage pages")}
            footer={renderActionsSection()}
            sections={[
              {
                title:
                  <div className="website-builder-manage-pages-view-menu-title">
                    {t("Menu")}
                    {pagesAutoSaveStatusSaveInProgress && (
                      <div className="manage-pages-view-menu-auto-save-spin-container">
                        <Spin />
                      </div>
                    )}
                  </div>,
                content: renderMenuSection(),
              },
            ]}
          />
        );
    }
  }

  return renderManagePagesView();
}
